import React, { ReactElement } from "react";

type Props = {
  value?: string | React.ReactElement | false | undefined | React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  extraClasses?: string;
  className?: string;
  icon?: ReactElement | false | undefined;
  iconPosition?: "start" | "end";
  extraProps?: React.ComponentProps<"button">;
  isPending?: boolean;
  type?: "submit" | "button" | "reset";
  variant?: "solid" | "outline";
  children?: React.ReactNode;
  title?: string;
  disabled?: boolean;
};

const ButtonStyled = ({
  value,
  onClick,
  extraClasses = "",
  className = "",
  icon,
  iconPosition = "start",
  extraProps,
  isPending = false,
  type = "button",
  variant = "solid",
  children,
  title,
  disabled,
}: Props) => {
  const content = value !== undefined && value !== false ? value : children;
  const isDisabled = isPending || disabled || extraProps?.disabled;

  const baseStyle =
    "group relative flex items-center justify-center gap-1 rounded-lg h-8 px-2 text-xs font-bold cursor-pointer overflow-hidden transition-all duration-500 ease-out active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 min-w-max";

  const variantStyles =
    variant === "outline"
      ? "bg-white border border-slate-300 text-[#131327] hover:border-[#FBD037] shadow-sm hover:shadow-md before:absolute before:inset-0 before:bg-[#FBD037] before:transition-transform before:duration-500 before:ease-in-out before:-translate-x-full hover:before:translate-x-0 after:absolute after:inset-x-0 after:top-0 after:h-1/2 after:bg-gradient-to-b after:from-white/40 after:to-transparent after:pointer-events-none after:transition-opacity after:duration-500 after:opacity-0 hover:after:opacity-100"
      : "bg-[#FBD037] border border-[#FBD037] text-[#131327] shadow-md before:absolute before:inset-0 before:bg-[#FBD037] before:transition-transform before:duration-500 before:ease-in-out before:translate-x-0 after:absolute after:inset-x-0 after:top-0 after:h-1/2 after:bg-gradient-to-b after:from-white/40 after:to-transparent after:pointer-events-none after:transition-opacity after:duration-500 after:opacity-100";

  const customClasses = className || extraClasses;

  return (
    <button
      onClick={onClick}
      {...extraProps}
      disabled={isDisabled}
      type={type}
      title={title}
      className={`${baseStyle} ${variantStyles} ${customClasses}`}
    >
      {isPending && (
        <svg
          className="relative z-10 animate-spin h-4 w-4 text-[#131327]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
          />
        </svg>
      )}
      {!isPending && icon && iconPosition === "start" && (
        <>
          <span className="relative z-10 shrink-0">{icon}</span>
          {content && (
            <span className="relative z-10 w-px h-3.5 shrink-0 bg-[#131327]/30 transition-colors duration-500" />
          )}
        </>
      )}
      {content && (
        <span className="relative z-10 shrink-0 whitespace-nowrap">
          {content}
        </span>
      )}
      {!isPending && icon && iconPosition === "end" && (
        <>
          {content && (
            <span className="relative z-10 w-px h-3.5 shrink-0 bg-[#131327]/30 transition-colors duration-500" />
          )}
          <span className="relative z-10 shrink-0">{icon}</span>
        </>
      )}
    </button>
  );
};

export default ButtonStyled;
